<template>
  <div :class="{'is-recap': rating && rating >= 4}" class="modal-frame">
    <div v-if="!rating" class="animated-content">
      <h3 class="title is-3">How was your event?</h3>
      <div :class="status" class="buttons has-addons is-centered">
        <div
          v-for="(emoji, key) in emojiScale"
          @click="startSaveRating(key + 1)"
          :key="'rating-' + key"
          class="button is-large"
        >
          {{ emoji }}
        </div>
      </div>
      <p>Your feedback helps us make Slides With Friends better and fix anything that didn't work smoothly. Thank you!</p>
    </div>
    <div v-if="rating && rating < 4">
      <template v-if="status !== 'feedback-sent'">
        <template v-if="rating < 3">
          <h3 class="title is-3">Oh no, what went wrong?</h3>
          <p>Slides With Friends should be fun and easy — tell us what didn't work for you and we'll do everything we can to fix it.</p>
        </template>

        <template v-if="rating === 3">
          <h3 class="title is-3">What can we do better?</h3>
          <p>Slides With Friends should be fun and easy — what we can do to make your next event better? </p>
        </template>

        <b-input v-model="feedback" maxlength="300" type="textarea" class="m-t-20 m-b-10" />
      </template>
      <template v-else>
        <h3 class="title is-3 m-t-20 m-b-10">Thank you, we appreciate it!</h3>
        <p class="m-b-20">We're a small team and your feedback makes a big difference for us. We'll take a look at your problem and get back to you shortly!</p>
        <br>
      </template>

      <div class="buttons is-right">
        <a :href="recapUrl" target="_blank" class="button is-text is-left">View Event Recap</a>
        <div @click="$emit('close')" class="button">Close</div>
        <div v-if="status !== 'feedback-sent'" @click="startSaveFeedback($event)" class="button is-primary">Send Feedback</div>
      </div>
    </div>

    <div v-if="rating && rating >=4">
      <template v-if="!sendFeedback">
        <h3 class="title is-3">👏 Thanks!&nbsp; Check out the recap:</h3>
        <!-- <p>Check it out and share with your team!</p> -->

        <div class="recap-share m-t-40">
          <div class="recap-thumb">
            <p class="recap-title">EVENT RECAP</p>
            <a :href="'/recap/' + eventId" v-if="slides.length > 0" target="_blank">
              <div class="inner-thumb">
                <SlideContainer
                  :active="true"
                  :slide="slides[0]"
                  :moderatorView="true"
                  :step="1"
                  :theme="slideDeck.themeData"
                  :event="event"
                  :recap="true"
                  :frozenScreenshot="true"
                  class="is-local small-slide-container"
                />
              </div>
            </a>
            <div class="share-links">
              <div @click="copyText(recapUrl)" class="button is-primary is-fullwidth">
                <i class="icon fas fa-link m-r-10" style="pointer-events:none;" />
                <p>Copy Link</p>
              </div>
              <ShareNetwork
                ref="twitterShare"
                :url="'https://slideswith.com/recap/' + eventId"
                :title="'Event recap — ' + title + ' hosted on Slides with Friends: '"
                :twitter-user="'SlidesWith'"
                network="twitter"
              >
                <div class="button is-twitter is-fullwidth m-t-10">
                  <i class="icon fab fa-twitter" style="pointer-events:none;" />
                  <p>Tweet</p>
                </div>
              </ShareNetwork>
            </div>
          </div>
        </div>
        <div @click="sendFeedback = true" class="button is-text is-feedback">Send us feedback</div>
      </template>
      <template v-else>
        <template v-if="status !== 'feedback-sent'">
          <h3 class="title is-3">What can we do better?</h3>
          <p>Your feedback is incredibly helpful for us as we work to add new features and improve Slides With Friends. Thank you!</p>

          <b-input v-model="feedback" maxlength="300" type="textarea" class="m-t-20 m-b-10" />
        </template>
        <template v-else>
          <h3 class="title is-3 m-t-20 m-b-10">Thank you, we appreciate it!</h3>
          <p class="m-b-20">We are a small team and every bit of feedback makes a big difference for us. Thank you!</p>
          <br>
        </template>

        <div class="buttons is-right">
          <div @click="sendFeedback = false" class="button is-text is-left">View Event Recap</div>
          <div @click="$emit('close')" class="button">Close</div>
          <div v-if="status !== 'feedback-sent'" @click="startSaveFeedback($event)" class="button is-primary">Send Feedback</div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
/* eslint-disable camelcase */
import { getEventRecap } from '~/queries/getEventRecap'
import { insertFeedback } from '~/queries/feedback.gql'

import SlideContainer from '~/components/SlideContainer'
import Slideshow from '~/components/mixins/Slideshow'

export default {
  name: 'PostEventModal',
  components: {
    SlideContainer
  },
  mixins: [Slideshow],
  props: ['eventId'],
  data () {
    return {
      isLocalPreview: true,
      question: 'How was your event?',
      questionId: 4, // database id of this question for Moderators
      emojiScale: ['😩', '😟', '😐', '😊', '😄'],
      rating: null,
      status: '',
      event: null,
      sendFeedback: false,
      feedback: ''
    }
  },
  apollo: {
    event: {
      query: getEventRecap,
      variables () {
        return {
          id: this.eventId
        }
      },
      update ({ events_by_pk }) {
        this.deckLoaded = true
        return events_by_pk
      }
    }
  },
  computed: {
    recapUrl () {
      return process.env.SITE_URL + `/recap/${this.eventId}`
    },
    slideDeck () {
      return this.event.slideDeckCopy || []
    },
    slides () {
      return this.slideDeck.slides || []
    },
    title () {
      const title = this.slides[0] && this.slides[0].content
        ? this.slides[0].content.title
        : this.shortDeckName

      return title + ' Recap'
    },
    user () {
      const user = { ...this.$store.state.user }

      const isPassActive = this.$store.getters.isPassActive
      const userPlan = this.$store.getters.userPlan
      const planType = isPassActive ? 'pass' : 'subscriber'

      user.plan = userPlan + ' ' + planType
      return user
    }
  },
  mounted () {
  },
  methods: {
    async startSaveRating (rating) {
      this.log('start save rating')
      this.status = 'sending-rating'
      this.rating = rating
      await this.saveFeedback(rating)
      this.status = 'rating-sent'
      this.trackResponse('rating')
    },
    async startSaveFeedback ($event) {
      if (this.status === 'feedback-sent') {
        this.$buefy.toast.open('Feedback already sent')
        return
      }
      this.status = 'sending-feedback'
      $event.target.classList.add('is-loading')
      await this.saveFeedback(this.feedback)
      await this.sendAdminEmail($event)
      $event.target.classList.remove('is-loading')
      $event.target.textContent = 'Feedback Sent!'
      this.status = 'feedback-sent'
      this.trackResponse('feedback')
    },
    async saveFeedback (feedback) {
      await this.$apollo.mutate({
        mutation: insertFeedback,
        variables: {
          response: feedback.toString(),
          questionId: this.questionId,
          userId: this.$auth.user ? this.$auth.user.id : null,
          questionText: this.question,
          eventId: this.eventId,
          url: process.env.SITE_URL + this.$route.path
        }
      })
    },
    trackResponse (type) {
      const trackingData = {
        event_id: this.event.id,
        shortcode: this.event.shortcode,
        deck_id: this.event.slideDeck
      }

      if (type === 'rating') {
        trackingData.rating = this.rating
      } else {
        trackingData.feedback = this.feedback
      }

      this.posthogCapture(`host_${type}_send`, trackingData)
    },
    async sendAdminEmail ($event) {
      const email = {
        to: 'hi@slideswith.com',
        replyTo: this.user.email,
        templateId: 27062980,
        tag: 'admin-feedback',
        variables: {
          location: 'Post-Event Modal',
          user: {
            id: this.user.id,
            email: this.user.email,
            plan: this.user.plan,
            stripe_customer: this.user.stripe_customer
          },
          event: {
            id: this.eventId
          },
          data: [
            { name: 'Rating', value: this.rating + '/5' },
            { name: 'Feedback', value: this.feedback }
          ]
        }
      }

      try {
        const resp = await this.$axios.post(process.env.SITE_URL + '/api/send-email-template', email)
        console.log(resp)
      } catch (e) {
        $event.target.classList.remove('is-loading')
        this.throwError(e.message)
      }
    },
    copyText (text) {
      const input = document.createElement('input')
      input.value = text
      document.body.appendChild(input)
      input.select()
      document.execCommand('Copy')
      input.remove()
      this.$buefy.toast.open({ message: 'Recap link copied!', type: 'is-primary' })
    }
  }
}
</script>

<style lang="scss">
.post-event-modal.modal {
  .modal-content {
    max-width: 600px;
  }
}

.is-slide.is-local {
  .event-details .flex-column {
    text-align: center !important;
    font-size: 1.2em;
    padding: 0 1em;
  }
  .join-box, .join-info {
    display: none;
  }

  .user-upload {
    max-width: 25em;
    max-height: 14em;
  }
}

.recap-thumb {
  .hero.is-slide {
    .join-link {
      display: none;
    }

    .direction.next {
      display: none;
    }
    .event-controls-wrap {
      display: none;
    }
  }

  .is-slide.slide-1 .slide-branding {
    opacity: 0;
  }
}
</style>

<style lang="scss" scoped>

.button.is-feedback {
  opacity: 1;
  text-decoration: none;
  color: #8e9eb1;
  margin-top: 5px;
  margin-bottom: -20px;

  &:hover {
    text-decoration: underline;
  }
}

.recap-title {
  // display: none;
  border-bottom: 1px solid #e3e8ee95;
  text-align: center;
  margin:-10px -30px 20px -30px;
  padding: 0 0 10px 0;
  // box-shadow: 1px 2px 10px -6px rgba(0,0,0,.15);
  font-size: .7em;
  text-transform: uppercase;
  font-weight: 500;
  color: #8e9eb1;
  // background-color: white;
}

.recap-thumb {

  .inner-thumb {
    position: relative;
    width: 400px;
    height: 250px;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
  }
}
.modal-frame {
    background-color: #fafafa;
    border-radius: 6px;
    overflow: hidden;
    padding: 50px 30px;
    transition: background-color .2s ease;

    &.is-recap {
      background-color: #f3f5f9;
    }
}

.post-event-modal .modal-frame {
  text-align: center;
}

.recap-share {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;

  .recap-thumb {
    flex-grow: 2;
    padding: 20px 30px;
    border-radius: 10px;
    background-color: white;
    box-shadow: 1px 5px 18px -8px rgba(0, 0, 0, .1);
  }

  .share-links {
    min-width: 150px;
    flex-shrink: 1;
    display: flex;
    flex-direction: row;
    margin-top: 20px;

    .button {
      margin: 0 10px;
      width: 180px;
    }
  }
}
</style>
